// ============================================================
// RegisterPage.js — Page de création de compte via lien d'invitation
// Accessible uniquement via ?invite=TOKEN (lien temporaire 48h).
// Props :
//   inviteToken — token d'invitation extrait de l'URL
//   onSuccess   — callback appelé après la création du compte
// ============================================================

import React, { useState, useEffect } from 'react';
import { API_URL } from './api';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';

const AppBar = () => (
    <div style={{ backgroundColor: '#1a5c38', padding: '12px 24px', display: 'flex', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
        <span style={{ color: 'white', fontWeight: 700, fontSize: '1.25rem', letterSpacing: '0.05em' }}>AVALIST</span>
    </div>
);

const RegisterPage = ({ inviteToken, onSuccess }) => {
    const [email,     setEmail]     = useState('');
    const [password,  setPassword]  = useState('');
    const [confirm,   setConfirm]   = useState('');
    const [showPwd,   setShowPwd]   = useState(false);
    const [error,     setError]     = useState('');
    const [loading,   setLoading]   = useState(false);
    const [checking,  setChecking]  = useState(true);
    const [tokenError, setTokenError] = useState('');
    const [done,      setDone]      = useState(false);

    // Vérifie la validité du token dès le montage
    useEffect(function() {
        fetch(API_URL + '/invite/' + encodeURIComponent(inviteToken))
            .then(function(r) { return r.json().then(function(b) { return { ok: r.ok, body: b }; }); })
            .then(function(res) {
                if (!res.ok) setTokenError(res.body.error || 'Lien invalide');
                else if (res.body.email) setEmail(res.body.email);
                setChecking(false);
            })
            .catch(function() {
                setTokenError('Impossible de vérifier le lien');
                setChecking(false);
            });
    }, [inviteToken]);

    var handleSubmit = function(e) {
        e.preventDefault();
        setError('');
        if (password !== confirm) { setError('Les mots de passe ne correspondent pas'); return; }
        if (password.length < 8)  { setError('Le mot de passe doit contenir au moins 8 caractères'); return; }

        setLoading(true);
        fetch(API_URL + '/register', {
            method  : 'POST',
            headers : { 'Content-Type': 'application/json' },
            body    : JSON.stringify({ token: inviteToken, email: email, password: password }),
        })
            .then(function(r) { return r.json().then(function(b) { return { ok: r.ok, body: b }; }); })
            .then(function(res) {
                if (!res.ok) { setError(res.body.error || 'Erreur lors de la création du compte'); setLoading(false); return; }
                setDone(true);
            })
            .catch(function() { setError('Erreur réseau'); setLoading(false); });
    };

    // Écran de vérification
    if (checking) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f0', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif' }}>
                <AppBar />
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Spinner animation="border" style={{ color: '#2d8a4e', width: '48px', height: '48px' }} />
                </div>
            </div>
        );
    }

    // Token invalide ou expiré
    if (tokenError) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f0', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif' }}>
                <AppBar />
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '32px 16px' }}>
                    <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.10)', padding: '40px 36px', maxWidth: '400px', width: '100%', textAlign: 'center' }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>🔗</div>
                        <h2 style={{ color: '#c0392b', fontWeight: 700, marginBottom: '12px', fontSize: '1.3rem' }}>Lien invalide</h2>
                        <p style={{ color: '#666', fontSize: '0.95rem' }}>{tokenError}</p>
                        <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '12px' }}>Demandez un nouveau lien à votre administrateur.</p>
                    </div>
                </div>
            </div>
        );
    }

    // Compte créé avec succès
    if (done) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f0', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif' }}>
                <AppBar />
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '32px 16px' }}>
                    <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.10)', padding: '40px 36px', maxWidth: '400px', width: '100%', textAlign: 'center' }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: '16px' }}>✅</div>
                        <h2 style={{ color: '#1a5c38', fontWeight: 700, marginBottom: '12px', fontSize: '1.3rem' }}>Compte créé !</h2>
                        <p style={{ color: '#555', marginBottom: '24px', fontSize: '0.95rem' }}>Votre compte a été créé avec succès. Vous pouvez maintenant vous connecter.</p>
                        <Button onClick={onSuccess} style={{ backgroundColor: '#2d8a4e', borderColor: '#2d8a4e', color: 'white', padding: '10px 28px', borderRadius: '8px', fontWeight: 600 }}>
                            Se connecter
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Formulaire de création de compte
    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f0', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, sans-serif' }}>
            <AppBar />
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '32px 16px' }}>
                <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.10)', padding: '40px 36px', width: '100%', maxWidth: '420px' }}>
                    <h2 style={{ color: '#1a5c38', fontWeight: 700, marginBottom: '6px', fontSize: '1.5rem' }}>Créer votre compte</h2>
                    <p style={{ color: '#666', marginBottom: '28px', fontSize: '0.92rem' }}>Complétez le formulaire pour activer votre accès AVALIST.</p>

                    {error && <Alert variant="danger" style={{ borderRadius: '8px', fontSize: '0.9rem' }}>{error}</Alert>}

                    <Form onSubmit={handleSubmit}>
                        <Form.Group style={{ marginBottom: '16px' }}>
                            <Form.Label style={{ fontWeight: 500, color: '#333', fontSize: '0.9rem' }}>Adresse e-mail</Form.Label>
                            <Form.Control
                                type="email"
                                value={email}
                                readOnly
                                onChange={function(e) { setEmail(e.target.value); }}
                                placeholder="prenom.nom@exemple.fr"
                                required
                                style={{ borderRadius: '8px', border: '1px solid #d0d0d0', padding: '10px 14px', fontSize: '0.95rem' }}
                            />
                        </Form.Group>

                        <Form.Group style={{ marginBottom: '16px' }}>
                            <Form.Label style={{ fontWeight: 500, color: '#333', fontSize: '0.9rem' }}>Mot de passe</Form.Label>
                            <div style={{ position: 'relative' }}>
                                <Form.Control
                                    type={showPwd ? 'text' : 'password'}
                                    value={password}
                                    onChange={function(e) { setPassword(e.target.value); }}
                                    placeholder="8 caractères minimum"
                                    required
                                    style={{ borderRadius: '8px', border: '1px solid #d0d0d0', padding: '10px 42px 10px 14px', fontSize: '0.95rem' }}
                                />
                                <button type="button" onClick={function() { setShowPwd(!showPwd); }}
                                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#888', fontSize: '1.1rem', lineHeight: 1 }}
                                    tabIndex="-1">
                                    {showPwd ? '🙈' : '👁️'}
                                </button>
                            </div>
                        </Form.Group>

                        <Form.Group style={{ marginBottom: '28px' }}>
                            <Form.Label style={{ fontWeight: 500, color: '#333', fontSize: '0.9rem' }}>Confirmer le mot de passe</Form.Label>
                            <Form.Control
                                type={showPwd ? 'text' : 'password'}
                                value={confirm}
                                onChange={function(e) { setConfirm(e.target.value); }}
                                placeholder="Répétez le mot de passe"
                                required
                                style={{ borderRadius: '8px', border: '1px solid #d0d0d0', padding: '10px 14px', fontSize: '0.95rem', borderColor: confirm && confirm !== password ? '#e74c3c' : '#d0d0d0' }}
                            />
                            {confirm && confirm !== password && (
                                <div style={{ color: '#e74c3c', fontSize: '0.82rem', marginTop: '4px' }}>Les mots de passe ne correspondent pas</div>
                            )}
                        </Form.Group>

                        <Button type="submit" disabled={loading} style={{ width: '100%', backgroundColor: '#2d8a4e', borderColor: '#2d8a4e', color: 'white', padding: '11px', borderRadius: '8px', fontWeight: 600, fontSize: '1rem' }}>
                            {loading
                                ? <span><Spinner animation="border" size="sm" style={{ marginRight: '8px' }} />Création en cours…</span>
                                : 'Créer mon compte'
                            }
                        </Button>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default RegisterPage;
